import React, { useEffect, useRef, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { completeZohoOAuth } from '../../hooks/useZohoIntegration';

type OutletContext = { userData?: { companyId?: string } };

const ZohoOAuthCallbackPage: React.FC = () => {
  const { userData } = useOutletContext<OutletContext>();
  const [params] = useSearchParams();
  const started = useRef(false);
  const [status, setStatus] = useState<'working' | 'success' | 'error'>('working');
  const [message, setMessage] = useState('Completing the secure Zoho connection...');

  useEffect(() => {
    const companyId = userData?.companyId;
    if (!companyId) return;
    if (started.current) return;
    started.current = true;
    const code = params.get('code');
    const state = params.get('state');
    const accountsServer = params.get('accounts-server');
    const location = params.get('location');
    const oauthError = params.get('error');

    if (oauthError) {
      setStatus('error');
      setMessage(`Zoho declined the connection: ${oauthError}`);
      return;
    }
    if (!code || !state || !accountsServer) {
      setStatus('error');
      setMessage('The Zoho callback is missing required information. Restart the connection.');
      return;
    }

    completeZohoOAuth({ companyId, code, state, accountsServer, location })
      .then(() => {
        setStatus('success');
        setMessage('Zoho is connected. You can now configure broker mappings.');
      })
      .catch((cause) => {
        setStatus('error');
        setMessage(cause instanceof Error ? cause.message : String(cause));
      });
  }, [params, userData?.companyId]);

  const Icon = status === 'working' ? Loader2 : status === 'success' ? CheckCircle2 : XCircle;
  return (
    <div className="min-h-[420px] flex items-center justify-center p-8">
      <div className="max-w-lg w-full rounded-lg border border-gray-700 bg-gray-900/60 p-8 text-center">
        <Icon className={`w-10 h-10 mx-auto mb-4 ${
          status === 'working'
            ? 'animate-spin text-blue-400'
            : status === 'success'
              ? 'text-emerald-400'
              : 'text-red-400'
        }`} />
        <h1 className="text-xl font-semibold text-white">Zoho integration</h1>
        <p className="mt-3 text-sm text-gray-300">{message}</p>
        {status !== 'working' && (
          <Link
            to="/settings/integrations"
            className="mt-6 inline-flex px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          >
            Return to Integrations
          </Link>
        )}
      </div>
    </div>
  );
};

export default ZohoOAuthCallbackPage;
